import { Location } from '@angular/common';
import { Component, Inject, Input, OnChanges, OnInit } from '@angular/core';
import { USER } from 'src/app/core/user.service';
import { sortCrewMembers } from 'src/app/flight-logs/flight-log-form-helper';
import { EmployeeHttpClient } from 'src/app/shared/flight-log-api/employees/employee-client.service';
import { CrewOnboardDto } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { FlightCadSummary } from 'src/store/flight-log/flight-log.model';
import { formatMomentDate } from 'src/util/dateLib';
import { round } from 'src/util/math';
import { humanize } from 'src/util/text';
import { getOrElse } from 'src/util/values';
import { FlightLogViewModel } from 'src/view-models/flight-log-view-model';
import { UserViewModel } from 'src/view-models/user-view-model';

@Component({
  selector: 'fl-view-flight-log',
  templateUrl: './view-flight-log.component.html',
  styleUrls: ['./view-flight-log.component.scss'],
})
export class ViewFlightLogComponent implements OnInit, OnChanges {
  @Input()
  public flightLog: FlightLogViewModel;
  @Input()
  public flightSummary?: FlightCadSummary;

  // flightDetailGroups: {label: string, value: keyof FlightLog | ((flightLog: FlightLog) => string)
  // }[][] = [];
  public flightDetails: string[][] = [[]];
  public crewMembers: string[][] = [[]];
  public pilotMetrics: string[][] = [[]];
  public maintenanceMetrics: string[][] = [[]];
  public actions: any;

  constructor(
    @Inject(USER) private _user: UserViewModel,
    private location: Location,
    public employees: EmployeeHttpClient,
  ) {}

  get features() {
    return this._user.flightLogFeatures;
  }

  get detailsTitle(): string {
    return this.flightLog.mode === 'EntryPerFlight' ? 'Details' : 'Legs';
  }

  get flightTime() {
    return this.flightLog.flightTime;
  }

  get isSimple(): boolean {
    return this.flightLog.mode === 'Simple';
  }

  public ngOnChanges(changes: any): void {
    this.createFlightDetails();
  }

  public ngOnInit() {
    this.crewMembers = sortCrewMembers(this.flightLog.flightCrew).map((c) => [
      c.role === 'CoPilot' ? 'Co-Pilot' : humanize(c.role),
      `${c.firstName} ${c.lastName}`.trim(),
      c.role.toLocaleLowerCase(),
    ]);
    this.pilotMetrics = [
      ['Total Trip Time', '???'],
      ['Total Trip Flight Time', '???'],
    ];
  }

  get entries() {
    return this.flightLog.entries.map((entry) => {
      const departureWaypoint = this.flightLog.waypoints.find(
        (w) => w.id === entry.entryDetail.fromWaypointId,
      );
      const departureDescription = departureWaypoint != null ? departureWaypoint.description : null;
      const departureTime = departureWaypoint != null ? departureWaypoint.departureTime : null;
      const arrivalWaypoint = this.flightLog.waypoints.find(
        (w) => w.id === entry.entryDetail.toWaypointId,
      );
      const arrivalDescription = arrivalWaypoint != null ? arrivalWaypoint.description : null;
      const arrivalTime = arrivalWaypoint != null ? arrivalWaypoint.arrivalTime : null;
      const entryDescription =
        this.flightLog.mode === 'EntryPerLeg'
          ? `${getOrElse(departureDescription, 'Unknown')} to ${getOrElse(
              arrivalDescription,
              'Unknown',
            )}`
          : 'Log Entry';

      return {
        entryDescription,
        logMode: this.flightLog.mode,
        flightTime: formatMomentDate(entry.flightTime, 'MM/DD/YYYY HH:mm'),
        departureDescription,
        departureTime:
          departureTime != null ? formatMomentDate(departureTime, 'HH:mm') : 'Awaiting Launch',
        arrivalDescription,
        arrivalTime:
          arrivalTime != null
            ? formatMomentDate(arrivalTime, 'HH:mm')
            : departureTime != null
            ? 'En Route'
            : 'N/A',
        started: departureTime != null,
        completed: arrivalTime != null,
        status:
          departureTime != null && arrivalTime != null
            ? 'entry-complete'
            : departureTime != null
            ? 'entry-active'
            : 'entry-not-started',
        flightCrew: entry.entryDetail.crewOnboard.map((crewOnboard) => {
          // tslint:disable-next-line: no-non-null-assertion
          const crewMember = this.flightLog.flightCrew.find(
            (c) => c.id === crewOnboard.flightCrewId,
          )!;

          let initials = 'Unknown';
          let name = 'Unknown';
          if (
            crewMember.firstName != null &&
            crewMember.firstName.length > 0 &&
            crewMember.lastName != null &&
            crewMember.lastName.length > 0
          ) {
            initials = crewMember.firstName[0] + crewMember.lastName[0];
            name = `${crewMember.firstName} ${crewMember.lastName}`;
          }

          return { initials, name };
        }),
        isPatientOnboard: entry.entryDetail.isPatientOnboard ? 'Y' : 'N',
        pilotEntries: entry.pilotLogEntries.map((pe) => {
          return {
            pilot: this.flightLog.flightCrew.find((c) => c.id === pe.flightCrewId),
            hnvgo: pe.hnvgo,
            landings: pe.landings,
            flightHours: pe.flightHours,
          };
        }),
      };
    });
  }

  public hasStat(val: number): boolean {
    return !!(val != null && val !== 0);
  }

  public statDisplay(val: number): number {
    return round(val, 1);
  }

  public getFlightCrewOnboard(crewOnboard: CrewOnboardDto[]) {
    return crewOnboard.reduce((display, _, index) => {
      if (index === 0) {
      }
      return '';
    }, '');
  }

  public back() {
    this.location.back();
  }

  public colorFor(crewRole: string) {
    if (crewRole.startsWith('pilot')) {
      return 'primary';
    }
    return crewRole.endsWith('pilot') ? 'accent' : '';
  }

  private createFlightDetails() {
    const f = this.flightLog;
    const s: Partial<FlightCadSummary> = this.flightSummary != null ? this.flightSummary : {};
    // ensure that aircraft and base are not null
    s.aircraft = Object.assign(getOrElse(s.aircraft, {}));
    s.transportBase = Object.assign(getOrElse(s.transportBase, {}));

    this.flightDetails = [
      ['Flight #', getOrElse(f.flightSummaryId, 'Unknown')],
      ['Base', s.transportBase != null ? getOrElse(s.transportBase.name, 'Unknown') : 'Unknown'],
      ['Aircraft', s.aircraft != null ? getOrElse(s.aircraft.tailNumber, 'Unknown') : 'Unknown'],
      ['Type', s.aircraft != null ? getOrElse(s.aircraft.aircraftType, 'Unknown') : 'Unknown'],
    ];
  }
}
